import { type DocumentNode, print, type GraphQLError } from 'graphql';
import { type VariablesOf, type ResultOf } from 'gql.tada';
import {
  isStellateEndpoint,
  resolveGraphqlMutationEndpoint,
  isGraphqlMutationDocument,
  getGraphqlOperationName,
} from '@firsttable/common';
import Logger from '@/lib/logger';

type GQLError = Prettify<GraphQLError & { status: string; code?: number }>;

const LOG_ENABLED = false;
const logger = new Logger('MAKE_REQUEST', 'LOG');
if (LOG_ENABLED) {
  logger.enable();
}

type MakeRequestConfig<QueryType extends DocumentNode> = {
  requestId: string;
  query: QueryType;
  variables: VariablesOf<QueryType>;
  headers: Headers;
  next?: NextFetchRequestConfig;
  cache?: RequestCache;
  abortSignal?: AbortSignal;
  tags?: string[];
};

/**
 * Helper function to make a GraphQL request
 */
export default async function makeRequest<QueryType extends DocumentNode>({
  requestId,
  query,
  variables,
  headers,
  next,
  cache,
  abortSignal,
  tags = [],
}: MakeRequestConfig<QueryType>): Promise<{ data: ResultOf<QueryType>; errors?: GQLError[] }> {
  const queryApiUrl = process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT;
  const apiUrl = isGraphqlMutationDocument(query)
    ? resolveGraphqlMutationEndpoint(queryApiUrl, process.env.NEXT_PUBLIC_GRAPHQL_MUTATION_ENDPOINT)
    : queryApiUrl;

  if (!apiUrl) {
    throw new Error('GraphQL API URL not set');
  }

  const requestHeaders = new Headers(headers);
  if (!isStellateEndpoint(apiUrl)) {
    requestHeaders.delete('x-graphql-rate-limiting-secret');
  }

  if (LOG_ENABLED) {
    logger.log(`${requestId} - Sending request`, {
      headers: Object.fromEntries([...requestHeaders.entries()]),
    });
  }

  const operationName = getGraphqlOperationName(query);

  const response = await fetch(apiUrl, {
    method: 'POST',
    body: JSON.stringify({
      query: print(query),
      variables,
      ...(operationName ? { operationName } : {}),
    }),
    headers: requestHeaders,
    next: {
      ...next,
      tags,
    },
    cache,
    signal: abortSignal,
  });

  if (LOG_ENABLED) {
    logger.log(`${requestId} - Response received`, {
      status: response.ok ? 'ok' : 'error',
      httpStatus: response.status,
    });
  }

  // check for success
  if (!response.ok) {
    if (LOG_ENABLED) {
      logger.log(`${requestId} - Network error`, {
        status: response.status,
        statusText: response.statusText,
      });
    }
    throw new Error(`Network error ${response.status}: "${response.statusText}"`);
  }

  return response.json();
}
